import {Signal} from "signals";
import {BigWinTierConfig} from "slotworks/config/component-config-schema";
import {ButtonEvent} from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import Currency from "slotworks/i18n/currency";
import {SoundEvent} from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contract";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";
import {parallel} from "slotworks/utils/race-utils";
import CampaignsModel from "slotworks/integration/revolver/model/campaigns-model";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import FlexiText from "slotworks/graphics/elements/flexi-text";

// todo - refactor, eventually combine with the other bigwin
export default class FreespinsBigWinComponent {

    public static BACKGROUND_SCENE: string = "bigWin";
    public static FOREGROUND_SCENE: string = "freespins_bigWin";

    public static HANG_TIME: number = 3000;
    public static CLICK_TO_SKIP: boolean = true;

    public static SHOW_CONTINUE_BUTTON: boolean = true;

    public countupMode: CountMode;
    // TODO (original big win): Implement non-upgrade mode
    public upgrade: boolean = true;

    public totalReached: Signal;

    private tiers: BigWinTierConfig[] = [];
    private currentTiers: BigWinTierConfig[];
    private valueTweenObject: { value: number };

    private continueButton: FlexiButton;
    private continueLabel: FlexiText;

    private showConfirmButton: boolean = true;

    constructor() {
        this.totalReached = new Signal();
    }

    public addWinTier(config: BigWinTierConfig) {

        this.tiers.push(config);

        this.tiers.sort((a, b) => a.threshold - b.threshold);
    }

    public upgradePending() {
        return this.currentTiers.length > 1;
    }

    public showWin(amount: number, stake: number): Contract<boolean> {

        return new Contract<boolean>((resolve) => {

            if (this.isBigWin(amount, stake)) {
                if(CampaignsModel.isFreespinsActive())
                {
                    Layers.PromoBetBar.hide();
                    Layers.SmartButton.hide();
                }
                this.currentTiers = this.getSelectedTiers(amount, stake);

                this.startCountup(amount, stake);
                let waitTimer: number;
                let countTimer: number;

                const activateButton = () => {
                    this.continueButton.setVisible(true);
                    this.continueLabel.setVisible(true);
                    this.continueButton.on(ButtonEvent.POINTER_DOWN, out);
                }

                const userSkip = () => {
                    TweenJS.removeAll(this.valueTweenObject);
                    Timer.clearTimeout(waitTimer);
                    Timer.clearTimeout(countTimer);

                    if (this.valueTweenObject) {
                        SoundManager.event(SoundEvent.big_win_countup_end);
                        this.valueTweenObject = null;
                    }

                    const valueText = Layers.CelebrationForeground.getText("value");
                    if (valueText) {
                        valueText.text = Currency.format(amount, undefined, undefined, true);
                    }

                    if (this.currentTiers.length > 1) {
                        this.currentTiers = [this.currentTiers[this.currentTiers.length - 1]];

                        upgrade(true);
                    } else if (this.currentTiers.length === 0) {
                        if(!this.showConfirmButton || !this.continueButton) {
                            out();
                        } else {
                            activateButton();
                        }
                    } else {
                        if(!this.showConfirmButton || !this.continueButton) {
                            waitTimer = Timer.setTimeout(out, this.currentTiers[0].waitTime);
                            this.currentTiers.shift();
                        } else {
                            activateButton();
                        }
                    }

                    Layers.CelebrationContent.skipTransition();
                };
                let done = false;
                const out = () => {
                    if(done)
                        return;
                    done = true;
                    if(this.continueButton && this.showConfirmButton) {
                        this.continueButton.off(ButtonEvent.POINTER_DOWN, out);
                        this.continueButton.setVisible(false);
                        this.continueLabel.setVisible(false);
                        this.continueButton.destroy();
                        this.continueButton = null;
                        this.continueLabel = null;
                    }
                    document.body.removeEventListener(ButtonEvent.POINTER_DOWN.getEventString(), userSkip, true);
                //    SoundManager.customEvent("celebration_end");
                    Layers.CelebrationContent.defaultScene().then(() => {
                        parallel([
                            () => Layers.CelebrationForeground.defaultScene(),
                            () => Layers.CelebrationBackground.defaultScene()
                        ]).then(() => {
                            resolve(true);
                        });
                    });


                };

                const upgrade = (skip?: boolean) => {
                    const currentTier = this.currentTiers[0];

                    Layers.CelebrationContent.jumpToScene("default");

                    Layers.CelebrationContent.setScene(currentTier.scene).execute();

                    countTimer = Timer.setTimeout(() => {
                        this.currentTiers.shift();

                        if (this.currentTiers.length) {
                            upgrade();
                        } else {
                            if(this.continueButton && this.showConfirmButton) {
                                activateButton();
                            } else {
                                waitTimer = Timer.setTimeout(out, currentTier.waitTime);
                            }
                        }
                    }, skip ? 0 : currentTier.countTime);
                };

                upgrade();

                if (FreespinsBigWinComponent.CLICK_TO_SKIP) {
                    document.body.addEventListener(ButtonEvent.POINTER_DOWN.getEventString(), userSkip, true);
                }
            } else {
                resolve(false);
            }

        });
    }

    public isBigWin(amount: number, stake: number): boolean {
        const multiplier = amount / stake;

        const selectedLevel = this.getSelectedTier(amount, stake);

        return selectedLevel !== null && selectedLevel.threshold <= multiplier;
    }

    private startCountup(amount: number, stake: number) {
        const selectedLevels = this.getSelectedTiers(amount, stake);

        let valueText: PIXI.Text;

        this.valueTweenObject = {value: 0};
        const countTween: TweenJS.Tween = new TweenJS.Tween(this.valueTweenObject);

        const update = () => {
            if (valueText) {
                valueText.text = Currency.format(this.valueTweenObject.value, undefined, undefined, true, true);
            }
        };

        const complete = () => {
            valueText.text = Currency.format(amount, undefined, undefined, true);

            SoundManager.event(SoundEvent.big_win_countup_end);

            this.totalReached.dispatch();
        };

        if (this.countupMode === CountMode.LINEAR) {

            let countTime = 0;
            for (const level of selectedLevels) {
                countTime += level.countTime;
            }
            countTween.to({value: amount}, countTime);
            countTween.onUpdate(update);
            countTween.onComplete(complete);

        } else {

            const finalLevel = selectedLevels.pop();
            let tween = countTween;
            countTween.to({value: 0}, 0);

            for (const level of selectedLevels) {
                tween.chain(tween = new TweenJS.Tween(this.valueTweenObject).to({value: level.threshold * stake}, level.countTime).onUpdate(update));
            }

            tween.chain(new TweenJS.Tween(this.valueTweenObject).to({value: amount}, finalLevel.countTime).onUpdate(update).onComplete(complete));
        }

        const setup = () => {
            valueText = Layers.CelebrationForeground.getText("value");
            valueText.text = "";

            this.continueButton = Layers.CelebrationForeground.getFlexiButton("continue");
            if(this.continueButton) {
                this.continueButton.setVisible(false);
                this.continueLabel = Layers.CelebrationForeground.getFlexiText("btn-continue-lbl");
                this.continueLabel.setVisible(false);
            }
            SoundManager.event(SoundEvent.big_win_countup_start);
        };

        if (Layers.CelebrationBackground.hasScene(FreespinsBigWinComponent.BACKGROUND_SCENE)) {
            Layers.CelebrationBackground.setScene(FreespinsBigWinComponent.BACKGROUND_SCENE).execute();
        }
        Layers.CelebrationForeground.setScene(FreespinsBigWinComponent.FOREGROUND_SCENE, setup).execute();

        countTween.start();
    }

    private getSelectedTier(amount: number, stake: number): BigWinTierConfig {
        const levels = this.getSelectedTiers(amount, stake);

        return levels.length ? levels[levels.length - 1] : null;
    }

    private getSelectedTiers(amount: number, stake: number): BigWinTierConfig[] {
        const multiplier = amount / stake;

        const selectedLevels: BigWinTierConfig[] = [];

        for (const level of this.tiers) {
            if (level.threshold > multiplier) {
                break;
            } else {
                selectedLevels.push(level);
            }
        }

        return selectedLevels;
    }
}

export enum CountMode {
    LINEAR = "linear",
    TIERED = "tiered"
}
